import React, { useState } from "react";
import { motion } from "framer-motion";
import { projects } from "../data/projects";

const allTags = Array.from(new Set(projects.flatMap(p => p.tags)));

interface ProjectsSectionProps {
  onImageClick: (image: { src: string; alt: string }) => void;
}

export default function ProjectsSection({ onImageClick }: ProjectsSectionProps) {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showAllTags, setShowAllTags] = useState(false);

  const filtered = selectedTag
    ? projects.filter(p => p.tags.includes(selectedTag))
    : projects;

  return (
    <section className="py-16 px-4 lg:px-8 text-white">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8 lg:mb-12">Проекты</h2>

        <div className="space-y-8">
          {filtered.map((project, index) => (
            <motion.div
              key={project.id}
              className="group bg-transparent p-6 rounded-xl transition-all duration-300 hover:bg-[#112240] cursor-pointer"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="lg:flex lg:gap-8">
                {/* Контент */}
                <div className="flex-1 flex flex-col">
                  <h3 className="text-2xl font-semibold mb-3 text-white group-hover:text-[#64ffda] transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-slate-300 mb-4 text-base leading-relaxed">
                    {project.description}
                  </p>

                  {/* Изображение для мобильных */}
                  <div className="mb-4 lg:hidden">
                    <div className="w-full rounded-lg overflow-hidden flex items-center justify-center">
                      {project.image ? (
                        <img 
                          src={project.image} 
                          alt={project.title}
                          className="w-full h-auto object-contain cursor-pointer hover:opacity-80 transition-opacity duration-200"
                          onClick={() => onImageClick({ src: project.image!, alt: project.title })}
                        />
                      ) : (
                        <div className="text-slate-400 text-xs text-center p-2 border border-slate-700 rounded-lg">
                          <svg className="w-8 h-8 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                          </svg>
                          Фото
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                        className={`text-sm px-3 py-1 rounded-full border transition-all duration-200 ${
                          selectedTag === tag 
                            ? "text-[#0a192f] bg-[#64ffda] border-[#64ffda]" 
                            : "text-[#64ffda] border-[#64ffda] hover:bg-[#64ffda] hover:text-[#0a192f]"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex gap-6">
                    <a 
                      href={project.github} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-[#64ffda] hover:text-white text-sm flex items-center gap-2 transition-colors duration-200 group-hover:translate-x-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                      </svg>
                      GitHub
                    </a>
                    {project.demo && (
                      <a 
                        href={project.demo} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-[#64ffda] hover:text-white text-sm flex items-center gap-2 transition-colors duration-200 group-hover:translate-x-1"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3m-2 16H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7z"/>
                        </svg>
                        Demo
                      </a>
                    )}
                  </div>
                </div>

                {/* Миниатюра для десктопа */}
                <div className="hidden lg:flex flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden items-center justify-center">
                  {project.image ? (
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity duration-200"
                      onClick={() => onImageClick({ src: project.image!, alt: project.title })}
                    />
                  ) : (
                    <div className="text-slate-400 text-xs text-center p-2">
                      <svg className="w-8 h-8 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                      </svg>
                      Фото
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="font-medium mb-4 text-lg text-slate-300">Фильтр по технологиям:</p>
          <div
            className={`flex flex-wrap justify-center gap-2 transition-all duration-300 ${
              !showAllTags ? "max-h-20 overflow-hidden" : "max-h-full"
            }`}
          >
            {/* Кнопка "Все" */}
            <button
              onClick={() => setSelectedTag(null)}
              className={`text-sm px-3 py-1 rounded-full border transition-all duration-200 flex items-center gap-1 ${
                selectedTag === null
                  ? "text-[#0a192f] bg-[#64ffda] border-[#64ffda]"
                  : "text-[#64ffda] border-[#64ffda] hover:bg-[#64ffda] hover:text-[#0a192f]"
              }`}
            >
              <span>Все</span>
              <svg
                className="w-3 h-3 text-red-500"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>

            {/* Технологии */}
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`text-sm px-3 py-1 rounded-full border transition-all duration-200 ${
                  selectedTag === tag
                    ? "text-[#0a192f] bg-[#64ffda] border-[#64ffda]"
                    : "text-[#64ffda] border-[#64ffda] hover:bg-[#64ffda] hover:text-[#0a192f]"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowAllTags(!showAllTags)}
            className="mt-4 text-sm text-[#64ffda] hover:text-white transition-colors duration-200"
          >
            {showAllTags ? "Скрыть" : "Показать все"}
          </button>
        </div>
      </div>
    </section>
  );
} 